import type { ITrendItem, ITrendDetail } from './types';

/** topic_type_l1 / l2 双语字段子集（List / Detail 共用） */
type TTopicTypeFields = Pick<
  ITrendItem | ITrendDetail,
  'topic_type_l1' | 'topic_type_l1_en' | 'topic_type_l2' | 'topic_type_l2_en'
>;

/**
 * 后端 topic_type_l1 / l2 字段命名约定：
 *   - `topic_type_l1`    → 中文
 *   - `topic_type_l1_en` → 英文
 *   - `topic_type_l2`    → 中文
 *   - `topic_type_l2_en` → 英文
 *
 * 取站点 UI 语言（isEn）展示对应字段，缺一侧时回退到另一侧。
 */
export function getTopicTypeLabels(
  record: TTopicTypeFields | null | undefined,
  isEn: boolean,
): { l1: string; l2: string } {
  if (!record) return { l1: '', l2: '' };
  const l1 = isEn
    ? record.topic_type_l1_en || record.topic_type_l1 || ''
    : record.topic_type_l1 || record.topic_type_l1_en || '';
  const l2 = isEn
    ? record.topic_type_l2_en || record.topic_type_l2 || ''
    : record.topic_type_l2 || record.topic_type_l2_en || '';
  return { l1, l2 };
}

/**
 * Topic Type Chip 配色 — 与 Figma「标签/彩色标签」组件（4626:64720）一致，共 21 色
 *
 * 同一 L1 父类目下的所有 L2 子类目共享 parent 的颜色（参考 Opinion TagList 的实现）。
 */
export interface IAITagColor {
  /** 前景色（边框 + 文字） */
  color: string;
  /** 背景色（chip 填充） */
  backgroundColor: string;
}

export const AI_TAGS_COLORS: IAITagColor[] = [
  { color: '#A23511', backgroundColor: '#F0DED9' },
  { color: '#CC5120', backgroundColor: '#F7E3DB' },
  { color: '#A1151E', backgroundColor: '#F0D9DB' },
  { color: '#F756A9', backgroundColor: '#FDE4F1' },
  { color: '#551DB0', backgroundColor: '#E4DBF2' },
  { color: '#8E51DA', backgroundColor: '#EDE3F9' },
  { color: '#B01BB6', backgroundColor: '#F2DAF3' },
  { color: '#77064F', backgroundColor: '#E9D7E3' },
  { color: '#B010B6', backgroundColor: '#F2D9F3' },
  { color: '#F6685D', backgroundColor: '#FDE7E5' },
  { color: '#FF7D00', backgroundColor: '#FFEAD6' },
  { color: '#D99F0A', backgroundColor: '#FBF3DD' },
  { color: '#774B04', backgroundColor: '#E9E2D7' },
  { color: '#72A611', backgroundColor: '#E8F1D9' },
  { color: '#1DB440', backgroundColor: '#DBF3E0' },
  { color: '#008026', backgroundColor: '#D6EADC' },
  { color: '#1FA6AA', backgroundColor: '#DBF1F1' },
  { color: '#029CD4', backgroundColor: '#D6EFF8' },
  { color: '#0E42D2', backgroundColor: '#D8E1F8' },
  { color: '#57A9FB', backgroundColor: '#E4F1FE' },
  { color: '#326FF0', backgroundColor: 'rgba(50, 111, 240, 0.1)' },
];

/**
 * Topic Type 颜色映射：`L1.label_en` → `IAITagColor`
 * 由 `useTopicTypeColorMap` 从 cond（`creative_trends_config_topic_types`）构建。
 */
export type TTopicTypeColorMap = Record<string, IAITagColor>;

/**
 * 根据 record 的 parent（L1 英文名）从 cond 颜色映射里拿配色。
 * - 通过 `topic_type_l1_en` 匹配 cond 的 `label_en`（英文更稳定，不受 UI 语言切换影响）
 * - cond 未加载 / 未命中 → 返回 undefined，调用方走默认样式
 */
export function getTopicTypeColor(
  record:
    | Pick<ITrendItem | ITrendDetail, 'topic_type_l1_en'>
    | null
    | undefined,
  colorMap: TTopicTypeColorMap | undefined | null,
): IAITagColor | undefined {
  if (!record || !colorMap) return undefined;
  const key = record.topic_type_l1_en;
  if (!key) return undefined;
  return colorMap[key];
}

export function getAiSummaryText(
  item: {
    ai_summary: string;
    ai_summary_en: string;
  } | null,
  isEn: boolean,
) {
  if (!item) {
    return '';
  }

  if (isEn) {
    return item.ai_summary_en || item.ai_summary || '';
  }

  return item.ai_summary || item.ai_summary_en || '';
}
